import { Button, Toast, type ToastType } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import {
  externalReferences,
  type ExternalReference,
  type ExternalReferenceEntityType,
} from "../../../api/addressBook.service";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";
import styles from "./ExternalReferencesPanel.module.css";

type Validator = (value: string) => { error: string | null; warning: string | null };

type ExternalReferencesPanelProps = {
  entityType: ExternalReferenceEntityType;
  entityId: string;
  canWrite: boolean;
  addableRefTypes: string[];
  validators?: Record<string, Validator>;
};

export function ExternalReferencesPanel({
  entityType,
  entityId,
  canWrite,
  addableRefTypes,
  validators,
}: ExternalReferencesPanelProps) {
  const [extRefs, setExtRefs] = useState<ExternalReference[]>([]);
  const [extRefsLoading, setExtRefsLoading] = useState(false);
  const [extRefsError, setExtRefsError] = useState<string | null>(null);
  const [newRefType, setNewRefType] = useState(addableRefTypes[0] ?? "");
  const [newRefValue, setNewRefValue] = useState("");
  const [newRefValidationError, setNewRefValidationError] = useState<string | null>(null);
  const [newRefWarning, setNewRefWarning] = useState<string | null>(null);
  const [isAddingRef, setIsAddingRef] = useState(false);
  const [deletingRefId, setDeletingRefId] = useState<string | null>(null);
  const [updatingRefId, setUpdatingRefId] = useState<string | null>(null);
  const [editingRefId, setEditingRefId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [savingRefId, setSavingRefId] = useState<string | null>(null);
  const [refToDelete, setRefToDelete] = useState<ExternalReference | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  async function loadExtRefs() {
    try {
      setExtRefsLoading(true);
      setExtRefsError(null);
      const response = await externalReferences.getByEntity(entityType, entityId);
      setExtRefs(response.data);
    } catch (err) {
      console.error("Failed to load external references:", err);
      setExtRefsError("Failed to load external references.");
    } finally {
      setExtRefsLoading(false);
    }
  }

  useEffect(() => {
    loadExtRefs();
  }, [entityType, entityId]);

  async function handleAddExtRef() {
    const validator = validators?.[newRefType];
    const { error: validationError, warning } = validator
      ? validator(newRefValue)
      : { error: null, warning: null };
    setNewRefWarning(warning);
    if (validationError) {
      setNewRefValidationError(validationError);
      return;
    }
    setNewRefValidationError(null);
    setIsAddingRef(true);
    try {
      await externalReferences.create(entityType, entityId, {
        reference_type: newRefType,
        reference_value: newRefValue.trim(),
      });
      setNewRefValue("");
      await loadExtRefs();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to add external reference.";
      setToast({ message, type: "error" });
    } finally {
      setIsAddingRef(false);
    }
  }

  async function confirmDeleteExtRef() {
    if (!refToDelete) return;
    setDeletingRefId(refToDelete.id);
    try {
      await externalReferences.delete(entityType, entityId, refToDelete.id);
      setRefToDelete(null);
      await loadExtRefs();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete external reference.";
      setRefToDelete(null);
      setToast({ message, type: "error" });
    } finally {
      setDeletingRefId(null);
    }
  }

  async function handleMakeCanonical(ref: ExternalReference) {
    setUpdatingRefId(ref.id);
    try {
      await externalReferences.update(entityType, entityId, ref.id, { is_canonical: true });
      await loadExtRefs();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update reference.";
      setToast({ message, type: "error" });
    } finally {
      setUpdatingRefId(null);
    }
  }

  function startEditRef(ref: ExternalReference) {
    setEditingRefId(ref.id);
    setEditValue(ref.referenceValue);
  }

  function cancelEditRef() {
    setEditingRefId(null);
    setEditValue("");
  }

  async function saveEditRef(ref: ExternalReference) {
    const trimmed = editValue.trim();
    if (!trimmed || trimmed === ref.referenceValue) {
      cancelEditRef();
      return;
    }
    setSavingRefId(ref.id);
    try {
      await externalReferences.update(entityType, entityId, ref.id, { reference_value: trimmed });
      cancelEditRef();
      await loadExtRefs();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update reference.";
      setToast({ message, type: "error" });
    } finally {
      setSavingRefId(null);
    }
  }

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>External IDs</h2>

      {extRefsLoading ? (
        <div className={styles.inlineLoader} role="status" aria-live="polite">
          <span className={styles.inlineLoaderSpinner} aria-hidden="true" />
          <span>Loading external references...</span>
        </div>
      ) : extRefsError ? (
        <span className={styles.errorText}>{extRefsError}</span>
      ) : (
        <table className={styles.rolesTable} style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "10rem" }} />
            <col style={{ minWidth: "24rem" }} />
            <col style={{ width: "8rem" }} />
            <col style={{ width: "20rem" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Type</th>
              <th>Value</th>
              <th>Canonical</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {extRefs.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.emptyText}>
                  No external references found.
                </td>
              </tr>
            ) : (
              extRefs.map(ref => {
                const isEditing = editingRefId === ref.id;
                const sameTypeCount = extRefs.filter(
                  r => r.referenceType === ref.referenceType,
                ).length;
                const isCanonicalAddressBook =
                  ref.referenceType === "address_book" && ref.isCanonical;
                const canDeleteRef =
                  canWrite && !isCanonicalAddressBook && (!ref.isCanonical || sameTypeCount === 1);
                const disabledDeleteTitle = isCanonicalAddressBook
                  ? "This is the entity's canonical identity and can't be deleted"
                  : `Delete the other ${ref.referenceType} references first, or promote another to canonical`;
                return (
                  <tr key={ref.id}>
                    <td>
                      <span className={styles.roleBadge}>{ref.referenceType}</span>
                    </td>
                    <td
                      style={{
                        fontFamily: "var(--font-family-mono, monospace)",
                        wordBreak: "break-all",
                      }}
                    >
                      {isEditing ? (
                        <input
                          type="text"
                          className={styles.formInput}
                          value={editValue}
                          aria-label={`Edit ${ref.referenceType} value`}
                          style={{
                            width: "100%",
                            height: "2.25rem",
                            boxSizing: "border-box",
                            padding: "0 var(--spacing-3)",
                          }}
                          onChange={e => setEditValue(e.target.value)}
                        />
                      ) : (
                        ref.referenceValue
                      )}
                    </td>
                    <td>
                      {ref.isCanonical && (
                        <span className={styles.roleBadge} aria-label="Canonical reference">
                          ★ Canonical
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                        {isEditing ? (
                          <>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              disabled={savingRefId === ref.id}
                              onClick={() => saveEditRef(ref)}
                              aria-label={`Save ${ref.referenceType} ${ref.referenceValue}`}
                            >
                              {savingRefId === ref.id ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              variant="outlined"
                              color="default"
                              size="small"
                              disabled={savingRefId === ref.id}
                              onClick={cancelEditRef}
                              aria-label={`Cancel editing ${ref.referenceType}`}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            {!ref.isCanonical && canWrite && (
                              <Button
                                variant="outlined"
                                color="default"
                                size="small"
                                disabled={updatingRefId === ref.id}
                                onClick={() => handleMakeCanonical(ref)}
                                aria-label={`Make canonical for ${ref.referenceType} ${ref.referenceValue}`}
                              >
                                {updatingRefId === ref.id ? "Saving..." : "Make canonical"}
                              </Button>
                            )}
                            {canWrite && (
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                onClick={() => startEditRef(ref)}
                                aria-label={`Edit ${ref.referenceType} ${ref.referenceValue}`}
                              >
                                Edit
                              </Button>
                            )}
                            {canDeleteRef ? (
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={deletingRefId === ref.id}
                                onClick={() => setRefToDelete(ref)}
                                aria-label={`Delete ${ref.referenceType} ${ref.referenceValue}`}
                              >
                                {deletingRefId === ref.id ? "Deleting..." : "Delete"}
                              </Button>
                            ) : (
                              canWrite && (
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  disabled
                                  title={disabledDeleteTitle}
                                  aria-label={`Delete ${ref.referenceType} ${ref.referenceValue}`}
                                >
                                  Delete
                                </Button>
                              )
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      )}

      {canWrite && (
        <div style={{ marginTop: "1.5rem" }}>
          <h3 className={styles.sectionTitle} style={{ fontSize: "var(--font-size-m)" }}>
            Add External ID
          </h3>
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <select
              className={styles.formInput}
              value={newRefType}
              onChange={e => setNewRefType(e.target.value)}
              aria-label="Reference type"
              style={{
                minWidth: "160px",
                height: "2.5rem",
                boxSizing: "border-box",
                padding: "0 var(--spacing-3)",
              }}
            >
              {addableRefTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                flex: 1,
                minWidth: "160px",
              }}
            >
              <input
                type="text"
                className={`${styles.formInput}${newRefValidationError ? ` ${styles.formInputError}` : ""}`}
                placeholder="Value"
                value={newRefValue}
                aria-label="Reference value"
                style={{
                  height: "2.5rem",
                  boxSizing: "border-box",
                  padding: "0 var(--spacing-3)",
                }}
                onChange={e => {
                  setNewRefValue(e.target.value);
                  setNewRefValidationError(null);
                  setNewRefWarning(null);
                }}
              />
              {newRefValidationError && (
                <span className={styles.errorText}>{newRefValidationError}</span>
              )}
              {!newRefValidationError && newRefWarning && (
                <span className={styles.helperText}>{newRefWarning}</span>
              )}
            </div>
            <Button
              variant="contained"
              color="primary"
              disabled={isAddingRef || !newRefValue.trim()}
              onClick={handleAddExtRef}
            >
              {isAddingRef ? "Adding..." : "Add"}
            </Button>
          </div>
        </div>
      )}

      {refToDelete && (
        <ConfirmationModal
          title="Delete external reference"
          message={`Are you sure you want to delete the ${refToDelete.referenceType} reference "${refToDelete.referenceValue}"?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setRefToDelete(null)}
          onConfirm={confirmDeleteExtRef}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}
